import type * as React from 'react'
import { useCallback } from 'react'
import type { JSX } from 'react'

import { Dialog, Popover } from '@buffer-mono/popcorn'

import { useCalendarMobileMode } from '~publish/components/Calendar/hooks/useCalendarMobileMode'

import styles from './ContentItemPopover.module.css'

type ContentItemPopoverProps = {
  children: React.ReactNode
  preview: React.ReactNode
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  side?: 'left' | 'right'
}

/**
 * Preview popover for a channel-less draft, mirroring CalendarPostPopover: a
 * Dialog on mobile, a Popover on desktop, both showing the `preview` card.
 * Signed-in callers pass ContentItemDetailsCard; the public schedule passes its
 * guest draft card, which needs no account data. Unlike the post popover it
 * needs no lazy load — the draft carries its full content already.
 */
export const ContentItemPopover = ({
  children,
  preview,
  isOpen,
  onOpenChange,
  side = 'right',
}: ContentItemPopoverProps): JSX.Element => {
  const isMobile = useCalendarMobileMode()

  // Close the preview when the draft is opened in the composer (the Edit /
  // actions carry the composer trigger attributes) or a link is followed,
  // matching CalendarPostPopover.
  const closeWhenComposerOpensOrLinkClicks = (
    evt: React.MouseEvent<HTMLElement>,
  ): void => {
    const target = evt.target
    if (!(target instanceof Element)) return

    if (
      target?.closest('a') ||
      target?.closest('[aria-controls="composer-root"]')
    ) {
      onOpenChange(false)
    }
  }

  const handlePointerDownOutside = useCallback(
    (e: CustomEvent<{ originalEvent: PointerEvent }>): void => {
      e.preventDefault()
      e.stopPropagation()
      queueMicrotask(() => onOpenChange(false))
    },
    [onOpenChange],
  )

  if (isMobile) {
    return (
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <Dialog.Trigger>{children}</Dialog.Trigger>
        <Dialog.Content
          className={styles.dialogContent}
          onClick={closeWhenComposerOpensOrLinkClicks}
          onPointerDownOutside={handlePointerDownOutside}
        >
          <Dialog.CloseButton />
          {preview}
        </Dialog.Content>
      </Dialog>
    )
  }

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <Popover.Trigger>{children}</Popover.Trigger>
      <Popover.Content
        side={side}
        align="start"
        className={styles.popoverContent}
        onClick={closeWhenComposerOpensOrLinkClicks}
      >
        {preview}
      </Popover.Content>
    </Popover>
  )
}
